'use client';

import { Form, Formik } from 'formik';
import { useIsTabletOrDesktop } from 'app/hooks/useMediaQuery';

import FormFieldCheckable from 'app/components/common/FormFieldCheckable';
import FormFieldSelect from 'app/components/common/FormFieldSelect';
import { IconCross } from 'app/components/sprites';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { getSearchParams } from 'app/utility/helpers';
import { useQuery } from '@tanstack/react-query';
// import getServices from 'app/actions/getServices';
import getSpecialists from 'app/actions/getSpecialists';
import getBranchesEssentials from 'app/actions/getBranchesEssentials';
import { TrainingTypeEnum } from '@/app/interfaces/schedule';

export interface IScheduleFilterFormFields {
  training_type?: string;
  branch: string;
  specialist?: string[];
  service?: string[];
}

function ScheduleFilterInner({
  onSubmit,
  initialState
}: {
  onSubmit?(values: IScheduleFilterFormFields): void;
  initialState: IScheduleFilterFormFields;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { data: branches } = useQuery({
    queryFn: async () => await getBranchesEssentials(),
    queryKey: ['branches']
  });

  // const { data: services } = useQuery({
  //   queryFn: async () =>
  //     await getServices({
  //       branchSlug: searchParams.get('branch') || undefined
  //     }),
  //   queryKey: ['services', searchParams]
  // });

  const { data: specialists } = useQuery({
    queryFn: async () =>
      await getSpecialists({
        branchSlug: searchParams.get('branch') || undefined,
        // Дропдаун использует только url превью — blurhash не нужен и не должен
        // считаться в браузере (лишний fetch + base64 на каждого специалиста).
        skipPlaceholders: true
      }),
    // Ключ должен зависеть от филиала. Сырой ReadonlyURLSearchParams React Query
    // хэширует в '{}', из-за чего список специалистов не обновлялся при смене филиала.
    queryKey: ['specialists', searchParams.get('branch') || '']
  });

  // const serviceOptions = useMemo(() => {
  //   if (!services?.items?.length) return [];
  //   return services.items.map(item => ({
  //     id: item.id,
  //     name: item.name,
  //     value: item.slug
  //   }));
  // }, [services]);

  const specialistOptions = useMemo(() => {
    if (!specialists?.items?.length) return [];
    return specialists.items.map(item => ({
      id: item.id,
      name: item.name,
      value: item.slug,
      image: item.image_preview?.[0]?.url
    }));
  }, [specialists]);

  const branchOptions = useMemo(() => {
    if (!branches?.length) return [];
    return branches.map(item => ({
      name: item.address || item.name,
      value: item.slug
    }));
  }, [branches]);

  const isTabletOrDesktop = useIsTabletOrDesktop();

  const trainingLabel = {
    individual: isTabletOrDesktop
      ? 'Индивидуальные тренировки'
      : 'Индивидуальная',
    group: isTabletOrDesktop ? 'Групповые тренировки' : 'Групповая'
  };

  const initialValues = useRef<IScheduleFilterFormFields>({
    training_type:
      searchParams.get('training_type') ||
      initialState.training_type ||
      TrainingTypeEnum.INDIVIDUAL,
    branch: searchParams.get('branch') || initialState.branch || '',
    specialist:
      searchParams.getAll('specialist') || initialState.specialist || [],
    service: searchParams.getAll('service') || initialState.service || []
  });

  const [currentValues, setCurrentValues] = useState(initialValues.current);

  const isInitialState = useMemo(() => {
    return !currentValues.specialist?.length && !currentValues.service?.length;
  }, [currentValues]);

  const submit = useCallback(
    (values: IScheduleFilterFormFields) => {
      setCurrentValues(values);
      router.replace(
        `${pathname}?${getSearchParams(searchParams, [
          { name: 'training_type', value: values.training_type },
          { name: 'branch', value: values.branch },
          { name: 'specialist', value: values.specialist },
          { name: 'service', value: values.service }
        ])}`,
        { scroll: false }
      );
    },
    [router, pathname, searchParams]
  );

  const isInitialSubmitDone = useRef(false);

  useEffect(() => {
    if (isInitialSubmitDone.current) return;
    isInitialSubmitDone.current = true;
    submit(initialValues.current);
  }, [initialValues, submit]);

  return (
    <Formik initialValues={initialValues.current} onSubmit={submit}>
      {({ values, submitForm, resetForm }) => (
        <Form
          onChange={submitForm}
          onReset={() => {
            const currentTrainigType = values.training_type;
            resetForm({
              values: {
                training_type: currentTrainigType,
                branch: branchOptions?.[0]?.value || '',
                specialist: [],
                service: []
              }
            });
            submitForm();
          }}
          className="md:flex md:gap-4 md:justify-between w-full wrapper-main wrapper-max text-brown relative z-10"
        >
          <div className="md:hidden grid grid-cols-2 gap-x-7 mb-11 pt-6 px-4 text-h2">
            <h2>Фильтры</h2>
            {!isInitialState && (
              <div className="pr-2.5">
                <button
                  type="reset"
                  className="button gap-2 text-h2 opacity-40 [&.active]:opacity-100 transition-opacity duration-300"
                >
                  <span>Сбросить</span>
                  <IconCross className="w-3 h-3 stroke-brown" />
                </button>
              </div>
            )}
          </div>

          <h3 className="md:hidden mb-3 px-4 text-h2">Тренировка</h3>

          <div className="flex justify-between items-center gap-6">
            <div className="flex flex-wrap gap-1.5 px-4 md:px-0 pb-5 md:pb-0 border-b md:border-none border-brown-20 w-full md:w-auto">
              <FormFieldCheckable
                name="training_type"
                type="radio"
                label={trainingLabel.individual}
                value={TrainingTypeEnum.INDIVIDUAL}
                className="checkable-button-dark"
              />
              <FormFieldCheckable
                name="training_type"
                type="radio"
                label={trainingLabel.group}
                value={TrainingTypeEnum.GROUP}
                className="checkable-button-dark"
              />
            </div>
            {/* {!isInitialState && (
              <button
                type="reset"
                className="p-2.5 gap-2 text-h2 opacity-40 [&.active]:opacity-100 transition-opacity duration-300 hidden md:button"
              >
                <span>Сбросить фильтр</span>
                <IconCross className="w-3 h-3 stroke-brown" />
              </button>
            )} */}
          </div>

          <div className="md:flex md:flex-col xl:flex-row md:gap-5 xl:gap-1.5 xl:justify-end xl:items-start">
            {branchOptions.length > 1 && (
              <div className="flex flex-wrap gap-1.5 pt-3 md:pt-0 pb-7 md:pb-0 px-4 md:px-0 border-b md:border-none border-brown-20">
                {branchOptions.map((item, index) => (
                  <FormFieldCheckable
                    key={index}
                    name="branch"
                    type="radio"
                    label={item.name}
                    value={item.value}
                    className="checkable-button-dark"
                  />
                ))}
              </div>
            )}

            <div className="flex flex-col md:flex-row md:flex-wrap md:justify-end gap-7 md:gap-1.5 mb-7 md:mb-0 pt-6 md:pt-0">
              {!isInitialState && (
                <button
                  type="reset"
                  className="p-2.5 gap-2 text-h2 opacity-40 [&.active]:opacity-100 transition-opacity duration-300 hidden md:button"
                >
                  <span>Сбросить фильтр</span>
                  <IconCross className="w-3 h-3 stroke-brown" />
                </button>
              )}
              <div className="w-full md:w-auto min-w-[186px]">
                <FormFieldSelect
                  name="specialist"
                  options={specialistOptions}
                  placeholder="Специалисты"
                  onChange={submitForm}
                  multiple={true}
                  showSelectedOptions={false}
                  showSelectedOptionCount={true}
                />
              </div>
              {/* <div className="w-full md:w-auto min-w-[186px]">
                <FormFieldSelect
                  name="service"
                  options={serviceOptions}
                  placeholder="Услуги"
                  onChange={submitForm}
                  multiple={true}
                  showSelectedOptions={false}
                  showSelectedOptionCount={true}
                />
              </div> */}
            </div>

            <button
              type="button"
              onClick={() => {
                submitForm();
                typeof onSubmit === 'function' && onSubmit(values);
              }}
              className="block w-full md:hidden py-3.5 px-5 text-h2 text-center border border-brown rounded"
            >
              Применить
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default function ScheduleFilter(props: {
  onSubmit?(values: IScheduleFilterFormFields): void;
  initialState: IScheduleFilterFormFields;
}) {
  return (
    <Suspense fallback={null}>
      <ScheduleFilterInner {...props} />
    </Suspense>
  );
}
